import { useThemeContext } from '#contexts/ThemeContext';
import { Answer, EQuestionType, QuestionnaireState, QuestionNode } from '#models/questionnaire';
import { Box, Button, makeStyles, MenuItem, TextField } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import { sortBy } from 'lodash-es';
import React from 'react';
import { BooleanAnswer } from './LeadGeneration/components/BooleanAnswer';
import { FieldAnswer } from './LeadGeneration/components/FieldAnswer';
import { useFieldStyles } from './LeadGeneration/hooks/field';

type FieldQuestionLayoutProps = {
  currentQuestion: QuestionNode;
  handleSelectAnswer: (value: Answer) => void;
  setQuestionnaireStateMerge: (questionnaireState: any) => void | Promise<any>;
  questionnaireState: QuestionnaireState;
};

export const FieldQuestionLayout = ({
  currentQuestion: { answers, id, type },
  handleSelectAnswer,
  setQuestionnaireStateMerge,
  questionnaireState
}: FieldQuestionLayoutProps) => {
  const classes = useStyles(type)();
  const { textField, menuItem } = useFieldStyles();
  const [{ value: answerSelectionIds = [] as string[] }, , { setValue: setAnswerSelections }] =
    useField('answerSelectionIds');
  const { handleSubmit, isSubmitting, status, setFieldValue } = useFormikContext();
  const [{ value: fieldAnswer }, { error: fieldAnswerError = {} as { [key: string]: any } }] = useField('fieldAnswer');

  const isRequiredQuestion = React.useMemo(() => answers.some((a) => a.required), [answers]);

  const answerSelected = React.useMemo(() => {
    const answer: Answer[] = answers.filter((answer: Answer) => answerSelectionIds.indexOf(answer.id) !== -1) || [];
    if (answer.length) {
      return answer[0];
    }
    return null;
  }, [answers, answerSelectionIds]);

  const virtualAnswer = answers.find((answer) => answer.isVirtual) || null;

  const hasError = React.useMemo(
    () =>
      typeof fieldAnswerError === 'object' && fieldAnswerError !== null
        ? answers.some((answer) => fieldAnswerError[answer.id])
        : false,
    [fieldAnswerError, answers]
  );

  const isDisabledButton = ((isRequiredQuestion || type === EQuestionType.Select) && !answerSelected) || hasError;

  const onChangeAnswer = (answer: Answer) => {
    let _answerSelections = [...answerSelectionIds];
    const foundIndex = _answerSelections.findIndex((i) => i === answer.id);
    if (foundIndex !== -1) {
      setTimeout(() =>
        setQuestionnaireStateMerge({
          ...questionnaireState,
          answerSelectionIds: _answerSelections,
          fieldAnswer
        })
      );
      return;
    }
    if (type !== EQuestionType.Name) {
      _answerSelections.push(answer.id);
    } else {
      answers.forEach((a) => {
        if (!_answerSelections.includes(a.id)) {
          _answerSelections.push(a.id);
        }
      });
    }
    setAnswerSelections(_answerSelections);
    if (type === EQuestionType.Boolean) {
      if (answer.nextQuestionId) {
        handleSelectAnswer(answer);
      } else handleSubmit();
    }
    setTimeout(() =>
      setQuestionnaireStateMerge({
        ...questionnaireState,
        fieldAnswer,
        answerSelectionIds: _answerSelections
      })
    );
  };

  const handleNextAnswer = () => {
    const _answerSelected = answerSelected ? { ...answerSelected } : !isRequiredQuestion ? answers[0] : null;
    if (!_answerSelected) {
      return;
    }
    if (_answerSelected.nextQuestionId) {
      handleSelectAnswer(_answerSelected);
      return;
    }
    handleSubmit();
  };

  return (
    <Box width='100%' marginX='auto' display='flex' flexDirection='column'>
      <Box
        width={550}
        maxWidth='90%'
        marginX='auto'
        display='flex'
        flexDirection={{ xs: 'column', md: 'row' }}
        className={classes.answerWrapper}
      >
        {type !== EQuestionType.Select &&
          type !== EQuestionType.Boolean &&
          sortBy(answers, 'sortOrder').map((answer: Answer) => (
            <Box
              key={answer.id}
              maxWidth={{ xs: 320, md: type === EQuestionType.Name ? 255 : 450 }}
              width='100%'
              mb={2}
            >
              <FieldAnswer
                answer={answer}
                questionId={id}
                type={type}
                onChangeAnswer={onChangeAnswer}
                multiline={type === EQuestionType.TextField}
              />
            </Box>
          ))}
        {type === EQuestionType.Select && (
          <TextField
            select
            fullWidth
            variant='outlined'
            className={`${textField} ${true ? '' : 'placeHolder'}`}
            value={answerSelected?.id ? answerSelected.id : virtualAnswer?.id ? virtualAnswer.id : null}
            onChange={(e) => {
              const answer = answers.find((a) => a.id === e.target.value);
              if (answer) {
                setFieldValue(`fieldAnswer.${answer.id}`, answer.text);
                onChangeAnswer(answer);
              }
            }}
            InputProps={{ className: 'lead-input swyft-input' }}
            SelectProps={{ classes: { select: 'lead-select' } }}
            size='small'
          >
            {sortBy(answers, 'sortOrder').map((answer: Answer) => {
              if (answer.isVirtual) {
                return (
                  <MenuItem value={answer.id} disabled className={`${menuItem} placeHolder`} key={answer.id}>
                    {answer.text}
                  </MenuItem>
                );
              }
              return (
                <MenuItem key={answer.id} value={answer.id} className={menuItem}>
                  {answer.text}
                </MenuItem>
              );
            })}
          </TextField>
        )}
        {type === EQuestionType.Boolean &&
          sortBy(answers, 'sortOrder').map((answer: Answer) => (
            <Box
              key={answer.id}
              mb={2}
              onClick={() => {
                setFieldValue(`fieldAnswer.${answer.id}`, answer.text);
                onChangeAnswer(answer);
              }}
              width='100%'
              display='flex'
              justifyContent='center'
            >
              <BooleanAnswer answer={answer} showHelperText={false} selected={answerSelectionIds.includes(answer.id)} />
            </Box>
          ))}
      </Box>
      {type !== EQuestionType.Boolean && (
        <Box
          width={320}
          height={42}
          maxWidth={'100%'}
          mt={type !== EQuestionType.Select ? 3 : 15}
          mx='auto'
          display='flex'
          justifyContent='center'
        >
          <Button
            disabled={isDisabledButton}
            variant='outlined'
            fullWidth
            className={classes.nextButton}
            onClick={handleNextAnswer}
            size='large'
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

const useStyles = (type: EQuestionType) => {
  const { primaryButtonBackground, primaryButtonText } = useThemeContext();
  return makeStyles((theme: any) => ({
    nextButton: {
      background: primaryButtonBackground,
      color: primaryButtonText,
      '&:hover': {
        backgroundColor: primaryButtonBackground,
        opacity: 0.8
      },
      '&.Mui-disabled': {
        backgroundColor: primaryButtonBackground,
        opacity: 0.5,
        color: primaryButtonText
      }
    },
    answerWrapper: {
      justifyContent: type === EQuestionType.Name ? 'space-around' : 'center',
      alignItems: type === EQuestionType.Name ? 'baseline' : 'center',
      flexDirection: type === EQuestionType.Boolean ? 'column' : 'row',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column'
      }
    }
  }));
};
